import { Property, Tenant, Invoice, Transaction, MaintenanceRequest, Employee, SalaryPayment, MaintenanceCompany, MaintenanceContract } from "../types";

// The local backend URL
// The API URL - use relative path in production for integrated deployment
export const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

async function fetchAPI<T>(endpoint: string): Promise<T> {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      window.location.reload();
      throw new Error('Unauthorized');
    }
    const errorBody = await response.json().catch(() => ({}));
    if (errorBody.error) throw new Error(errorBody.error);
    throw new Error(`API Error ${response.status}: ${response.statusText} at ${endpoint}`);
  }
  return await response.json();
}

async function postAPI<T>(endpoint: string, data: any): Promise<T> {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/${endpoint}`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}` 
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    if (errorBody.error) throw new Error(errorBody.error); // Show backend error message (e.g. Validation)
    
    throw new Error(`API Error ${response.status}: ${response.statusText} at ${endpoint}`);
  }
  return await response.json();
}
async function putAPI<T>(endpoint: string, data: any): Promise<T> {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/${endpoint}`, {
    method: 'PUT',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}` 
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    if (errorBody.error) throw new Error(errorBody.error);
    throw new Error(`API Error ${response.status}: ${response.statusText} at ${endpoint}`);
  }
  return await response.json();
}


export const api = {
  auth: {
    login: async (username: string, password: string): Promise<{ id: string; name: string } | null> => {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          if (errorBody.error) {
              throw new Error(errorBody.error);
          }
          if (response.status === 401) {
              throw new Error('بيانات الدخول غير صحيحة');
          }
          throw new Error('فشل تسجيل الدخول');
      }
      const data = await response.json();
      if (data.success && data.user) {
        localStorage.setItem('token', data.token);
        return data.user;
      }
      throw new Error('بيانات الدخول غير صحيحة');
    }
  },
  properties: {
    getAll: async (): Promise<Property[]> => {
      return fetchAPI('properties');
    },
    create: async (data: Property): Promise<Property> => {
      return postAPI('properties', data);
    },
    update: async (id: string, data: Property): Promise<Property> => {
      const response = await fetch(`${API_URL}/properties/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Update failed');
      return response.json();
    }
  },
  tenants: {
    getAll: async (): Promise<Tenant[]> => {
      const results = await fetchAPI<any[]>('tenants');
      return results.map(r => ({
        id: r.id,
        firstName: r.first_name || r.firstName, // fallback if backend returns camelCase directly
        lastName: r.last_name || r.lastName,
        email: r.email,
        mobile: r.mobile,
        type: r.type,
        language: r.language || 'Arabic',
        civilId: r.civil_id || r.civilId || '',
        nationality: r.nationality || '',
        contractStart: r.contract_start || r.contractStart ? String(r.contract_start || r.contractStart).split('T')[0] : '',
        contractEnd: r.contract_end || r.contractEnd ? String(r.contract_end || r.contractEnd).split('T')[0] : '',
        predictedLeaving: r.predicted_leaving || '',
        hasLegalCase: Boolean(r.has_legal_case || r.hasLegalCase),
        sendWelcomeEmail: Boolean(r.send_welcome_email || r.sendWelcomeEmail),
        sendWelcomeSMS: Boolean(r.send_welcome_sms || r.sendWelcomeSMS),
        rentPaymentDay: r.rent_payment_day || r.rentPaymentDay,
        paymentCycle: r.payment_cycle || r.paymentCycle,
        unit_number: r.unit_number,
        property_name: r.property_name,
        assignedUnits: r.assignedUnits || []
      }));
    },
    create: async (data: Tenant): Promise<Tenant> => {
      return postAPI('tenants', data);
    },
    update: async (id: string, data: Tenant): Promise<Tenant> => {
      return putAPI(`tenants/${id}`, data);
    }
  },
  invoices: {
    getAll: async (): Promise<Invoice[]> => {
      return fetchAPI('invoices');
    },
    pay: async (id: string, data: any): Promise<void> => {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/invoices/${id}/pay`, {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` 
            },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to pay invoice');
    },
    create: async (data: any): Promise<Invoice> => {
        return postAPI('invoices', data);
    }
  },
  transactions: {
    getAll: async (): Promise<Transaction[]> => {
      return fetchAPI('transactions');
    },
      create: async (data: Partial<Transaction>): Promise<Transaction> => {
          return postAPI('transactions', data);
      },
      linkToInvoice: async (transactionId: string, invoiceId: string): Promise<any> => {
          return putAPI(`transactions/${transactionId}/link`, { invoiceId });
      }
  },
  maintenance: {
    getAll: async (): Promise<MaintenanceRequest[]> => {
      return fetchAPI('maintenance');
    },
    create: async (data: MaintenanceRequest): Promise<MaintenanceRequest> => {
        return postAPI('maintenance', data);
    },
    updateStatus: async (id: string, data: any): Promise<void> => {
        return putAPI(`maintenance/${id}/status`, data);
    },
    companies: {
        getAll: async (): Promise<MaintenanceCompany[]> => {
            return fetchAPI('maintenance/companies');
        },
        create: async (data: MaintenanceCompany): Promise<MaintenanceCompany> => {
            return postAPI('maintenance/companies', data);
        },
        update: async (id: string, data: MaintenanceCompany): Promise<void> => {
            return putAPI(`maintenance/companies/${id}`, data);
        },
        delete: async (id: string): Promise<void> => {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/maintenance/companies/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Delete failed');
        }
    },
    contracts: {
        getAll: async (): Promise<MaintenanceContract[]> => {
            return fetchAPI('maintenance/contracts');
        },
        create: async (data: MaintenanceContract): Promise<MaintenanceContract> => {
            return postAPI('maintenance/contracts', data);
        },
        update: async (id: string, data: MaintenanceContract): Promise<void> => {
            return putAPI(`maintenance/contracts/${id}`, data);
        },
        delete: async (id: string): Promise<void> => {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/maintenance/contracts/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Delete failed');
        }
    }
  },
  units: {
      create: async (data: any): Promise<any> => {
          return postAPI('units', data);
      }
  },
  employees: {
      getAll: async (): Promise<Employee[]> => {
          return fetchAPI('employees');
      },
      create: async (data: Partial<Employee>): Promise<Employee> => {
          return postAPI('employees', data);
      },
      update: async (id: string, data: Partial<Employee>): Promise<Employee> => {
          return putAPI(`employees/${id}`, data);
      }
  },
  salaries: {
      getAll: async (): Promise<SalaryPayment[]> => {
          return fetchAPI('salaries');
      },
      create: async (data: Partial<SalaryPayment>): Promise<SalaryPayment> => {
          return postAPI('salaries', data);
      }
  },
  users: {
      getAll: async (): Promise<any[]> => {
          return fetchAPI('users');
      },
      create: async (data: any): Promise<any> => {
          return postAPI('users', data);
      },
      update: async (id: string, data: any): Promise<void> => {
          return putAPI(`users/${id}`, data);
      },
      delete: async (id: string): Promise<void> => {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/users/${id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!response.ok) throw new Error('Delete failed');
      }
  },
  transactionCategories: {
      getAll: async (): Promise<any[]> => {
          return fetchAPI('transaction-categories');
      },
      create: async (data: any): Promise<any> => {
          return postAPI('transaction-categories', data);
      },
      delete: async (id: string): Promise<void> => {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/transaction-categories/${id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!response.ok) {
              const errorBody = await response.json().catch(() => ({}));
              throw new Error(errorBody.error || 'Delete failed');
          }
      }
  },
  companyInfo: {
    get: async (): Promise<any> => {
      return fetchAPI('company-info');
    },
    update: async (data: any): Promise<any> => {
      return putAPI('company-info', data);
    }
  },
  accounting: {
    getTreasuryBalance: async (): Promise<{ balance: number; total_debit: number; total_credit: number }> => {
      return fetchAPI('accounting/treasury-balance');
    },
    getPlReport: async (year: string, propertyId: string): Promise<any> => {
      return fetchAPI(`accounting/pl-report?year=${year}&propertyId=${propertyId}`);
    }
  },
  attachments: {
    getByEntity: async (entityType: string, entityId: string): Promise<any[]> => {
      return fetchAPI(`attachments/${entityType}/${entityId}`);
    },
    getData: async (id: string): Promise<any> => {
      return fetchAPI(`attachments/${id}/data`);
    },
    upload: async (data: { entity_type: string; entity_id: string; file_name: string; file_data: string; file_type: string }): Promise<any> => {
      return postAPI('attachments', data);
    },
    delete: async (id: string): Promise<void> => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/attachments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Delete failed');
    }
  }
};